import { Component, EventEmitter, Inject, Output } from "@angular/core";
import { ConfirmationModalData } from "src/app/shared/interfaces/confirmation-modal-data";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { take } from "rxjs/operators";

@Component({
  selector: "confirmation-modal",
  template: `
    <div class="d-flex flex-row gap-16">
      <div [ngSwitch]="data.icon" class="confirmation-modal-icon">
        <key-icon
          *ngSwitchCase="'success'"
          class="confirmation-modal-icon-image"
          size="lg"
          theme="light-outline"
          kind="success"
          icon="check"
        />
        <key-icon
          *ngSwitchCase="'warning'"
          class="confirmation-modal-icon-image"
          size="lg"
          theme="light-outline"
          kind="warning"
          set="typcn"
          icon="warning-outline"
        />
        <key-icon
          *ngSwitchCase="'info'"
          class="confirmation-modal-icon-image"
          size="lg"
          theme="light-outline"
          kind="primary"
          icon="info"
          set="typcn"
        />
        <key-icon
          *ngSwitchCase="'error'"
          class="confirmation-modal-icon-image"
          size="lg"
          theme="light-outline"
          kind="error"
          set="typcn"
          icon="cancel-outline"
        />
      </div>
      <div class="confirmation-modal">
        <div class="confirmation-modal-content">
          <h4 class="confirmation-modal-title color-bg-text-primary">
            {{ data.modalTitle | translate: data.translationParams }}
          </h4>
          <p
            *ngIf="data.message"
            class="confirmation-modal-text text-body-sm color-bg-text-secondary"
            [innerHTML]="
              data.message | translate: data.translationParams | replaceNewLine
            "
          ></p>
          <div class="confirmation-modal-buttons w-100">
            <button
              *ngIf="!data.cancelButtonHide"
              type="button"
              class="btn-secondary"
              [disabled]="loading"
              (click)="cancel()"
            >
              {{ data.cancelButtonText || "GLOBAL.CANCEL_BUTTON" | translate }}
            </button>

            <button
              [ngClass]="confirmButtonClass"
              type="button"
              class="btn-primary btn-loading"
              [class.is-loading]="loading"
              [disabled]="loading"
              (click)="confirm()"
            >
              <cs-loader
                *ngIf="loading"
                class="btn-loading__spinner"
                [size]="16"
                [stroke]="2"
              />
              <span class="btn-loading__label">
                {{ data.confirmButtonText || "GLOBAL.OK_BUTTON" | translate }}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrls: ["./confirmation-modal.component.scss"],
})
export class ConfirmationModalComponent {
  loading = false;

  constructor(
    public dialogRef: MatDialogRef<ConfirmationModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmationModalData,
  ) {}

  get confirmButtonClass(): string {
    switch (this.data.confirmButtonColor) {
      case "primary":
        return "btn-primary";
      case "secondary":
        return "btn-blue-light-900";
      case "warning":
        return "btn-warning";
      case "success":
        return "btn-success";
      case "error":
        return "btn-error";
      default:
        return "btn-success";
    }
  }

  confirm(): void {
    if (this.loading) {
      return;
    }

    if (!this.data.confirmAction) {
      this.dialogRef.close(true);
      return;
    }

    this.loading = true;
    this.dialogRef.disableClose = true;

    this.data
      .confirmAction()
      .pipe(take(1))
      .subscribe({
        next: (result) => this.dialogRef.close(result ?? true),
        error: () => {
          this.loading = false;
          this.dialogRef.disableClose = false;
        },
      });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
